import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getConnectionStatus, subscribeConnection } from '../utils/connectionStatus';
import { SIGNER_ROLES, errorMessage, hasRequiredDocuments, withDocumentStatus } from '../utils/staffDocuments';
import DocumentEditor from '../components/staffDocuments/DocumentEditor';
import PdfPanel from '../components/staffDocuments/PdfPanel';
import SignBox from '../components/staffDocuments/SignBox';
import Signatures from '../components/staffDocuments/Signatures';
import './booking/booking-shared.css';
import './StaffDocuments.css';

// Written documents are kept on the phone so they can be read with no signal.
// Kept per person, since a copy carries that person's own signature.
const cacheKey = (user, id) => `staffDocument:${user?.clubId || ''}:${user?.id || ''}:${id}`;
const readCache = (user, id) => {
  try { return JSON.parse(localStorage.getItem(cacheKey(user, id))); } catch { return null; }
};
const writeCache = (user, id, doc) => {
  try { localStorage.setItem(cacheKey(user, id), JSON.stringify(doc)); } catch { /* storage unavailable */ }
};

// One staff document: read it, sign it, and (for club admins) edit it, see who
// has signed, require it or archive it.
export default function StaffDocument() {
  const { id } = useParams();
  const { user, updateUser, can } = useAuth();
  // Staff who manage staff documents add, edit, archive and see who has signed
  const managesDocuments = can('staffDocuments', 'MANAGE');
  const [doc, setDoc] = useState(() => readCache(user, id));
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [online, setOnline] = useState(() => getConnectionStatus() !== 'offline');
  useEffect(() => subscribeConnection(s => setOnline(s !== 'offline')), []);

  const allowed = can('staffDocuments') || hasRequiredDocuments(user);
  useEffect(() => {
    if (!allowed) return;
    axios.get(`/api/staff-documents/${id}`)
      .then(res => {
        setDoc(res.data);
        if (res.data.kind === 'TEXT') writeCache(user, id, res.data);
        setLoadError(null);
      })
      .catch(err => setLoadError(errorMessage(err, 'Could not load this document.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, id]);

  // After a save or reset the version may have changed, so ask again what
  // this person still has to sign
  const refreshStatus = async (saved) => {
    setDoc(saved);
    if (saved.kind === 'TEXT') writeCache(user, id, saved);
    try {
      const res = await axios.get('/api/staff-documents/mine');
      updateUser({ ...user, staffDocuments: res.data });
    } catch { /* the next sign in catches up */ }
  };

  const setArchived = async (archived) => {
    setActionError(null);
    try {
      const res = await axios.put(`/api/staff-documents/${id}/archived`, { archived });
      setDoc(d => ({ ...d, archived: res.data.archived, required: res.data.required }));
      updateUser(withDocumentStatus(user, id, { required: res.data.required }));
    } catch (err) {
      setActionError(errorMessage(err, 'Could not save.'));
    }
  };

  if (!allowed) return <Navigate to="/dashboard" replace />;
  if (!doc) {
    return (
      <div className="bk-page coc-page">
        <p><Link to="/staff-documents">Staff documents</Link></p>
        {loadError ? <p className="bk-error">{loadError}</p> : <p className="text-muted">Loading…</p>}
      </div>
    );
  }

  const status = user?.staffDocuments?.documents?.find(d => d.id === doc.id);
  const signed = doc.signed || null;
  const canSign = SIGNER_ROLES.includes(user?.role) && doc.signerRoles.includes(user.role) && !doc.archived;
  const mustSign = Boolean(status?.required) && !signed && !doc.archived;

  if (editing) {
    return (
      <div className="bk-page coc-page">
        <DocumentEditor doc={doc} online={online} onCancel={() => setEditing(false)}
          onSaved={saved => { setEditing(false); refreshStatus(saved); }} />
      </div>
    );
  }

  return (
    <div className="bk-page coc-page">
      <p><Link to="/staff-documents">Staff documents</Link></p>
      <div className="coc-head">
        <h2>{doc.title}</h2>
        {managesDocuments && (
          <button type="button" className="bk-btn bk-btn--sm" onClick={() => setEditing(true)} disabled={!online}>Edit</button>
        )}
      </div>
      <p className="coc-version">Version: {doc.versionLabel}{doc.archived ? '. Archived' : ''}</p>
      {mustSign && (
        <p className="coc-required" role="alert">The club needs you to read and sign this before you can use staff pages.</p>
      )}

      {doc.kind === 'FILE' ? <PdfPanel doc={doc} /> : (
        <>
          {doc.intro.map(p => <p key={p}>{p}</p>)}
          {doc.sections.map((section, i) => (
            <section key={`${i}-${section.heading}`} className="coc-section">
              <h3>{i + 1}. {section.heading}</h3>
              <ul>{section.items.map(item => <li key={item}>{item}</li>)}</ul>
            </section>
          ))}
          {doc.followsTemplate && (
            <p className="coc-bg-link">
              Read British Gymnastics' Standards and Expectations on the{' '}
              <a href="https://www.british-gymnastics.org/gymnasts/gymnast-membership/safeguarding-and-compliance" target="_blank" rel="noopener noreferrer">British Gymnastics website</a>.
            </p>
          )}
        </>
      )}

      {canSign && (
        <SignBox
          doc={doc}
          signed={signed}
          online={online}
          onSigned={signature => {
            setDoc(d => ({ ...d, signed: signature }));
            updateUser(withDocumentStatus(user, doc.id, { signed: true, version: doc.version }));
          }}
        />
      )}
      {managesDocuments && (
        <Signatures
          doc={doc}
          ownSigned={Boolean(signed)}
          online={online}
          onRequiredChange={required => {
            setDoc(d => ({ ...d, required }));
            updateUser(withDocumentStatus(user, doc.id, { required }));
          }}
        />
      )}
      {managesDocuments && (
        <div className="coc-archive">
          {actionError && <p className="bk-error">{actionError}</p>}
          <button type="button" className="bk-btn bk-btn--sm" onClick={() => setArchived(!doc.archived)} disabled={!online}>
            {doc.archived ? 'Restore' : 'Archive'}
          </button>
          <small className="text-muted">
            {doc.archived ? 'Restoring puts it back on the list. It is not required until you switch that on.' : 'Archiving hides it from the list and stops it being required.'}
          </small>
        </div>
      )}
    </div>
  );
}
